/*
 * NC Floating Header — state machine + mega menu + mobile drawer.
 *
 * data-header-state: top | transition | compact | scrolling-down | scrolling-up
 * data-compact:      false | true   (drives the CSS)
 * data-header-dir:   idle | down | up
 *
 * Thresholds/timing were measured on the reference site: it goes compact a little
 * past ~66px of scroll, returns below ~60px, and never hides on scroll direction.
 * Only transform/opacity are animated. Scroll work runs in requestAnimationFrame.
 *
 * Scroll source: this theme scrolls inside .page-wrapper on desktop (html/body are overflow:hidden)
 * and on the window on mobile. measure() picks whichever is actually scrollable so a stale window
 * scroll offset can never leave the header stuck in the compact state.
 */
const ENTER_Y = 66;
const EXIT_Y = 60;
const DIR_THRESHOLD = 6;
const DESKTOP_MIN = 1000; /* keep in sync with the media queries in assets/nc-floating-header.css */

function cssMs(el, name, fallback) {
  const raw = getComputedStyle(el).getPropertyValue(name).trim();
  const n = parseFloat(raw);
  if (Number.isNaN(n)) return fallback;
  return raw.endsWith('ms') ? n : n * 1000;
}

class NCFloatingHeader extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.wrap = this.querySelector('.ncfh');
    this.drawer = this.querySelector('[data-ncfh-drawer]');
    this.menuBtn = this.querySelector('[data-ncfh-open]');
    this.pageWrapper = document.querySelector('.page-wrapper');
    this.usePageWrapper = false;
    this.compact = false;
    this.state = 'top';
    this.lastY = 0;
    this.offset = -1;
    this.announceH = 0;
    this.ticking = false;
    this.settleTimer = 0;
    this.idleTimer = 0;
    this.closeTimer = 0;
    this.hoverTimer = 0;
    this.openItem = null;
    this.drawerOpen = false;
    this.returnFocus = null;

    this.measure();
    this.lastY = this.getY();
    this.update(true);
    requestAnimationFrame(() => requestAnimationFrame(() => this.setAttribute('data-ready', '')));

    document.addEventListener(
      'scroll',
      (event) => {
        if (event.target !== document && event.target !== this.pageWrapper) return;
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.update(false);
        });
      },
      { passive: true, capture: true }
    );
    window.addEventListener('resize', () => this.onResize(), { passive: true });
    window.addEventListener('load', () => this.onResize());
    window.addEventListener('pageshow', () => {
      this.measure();
      this.lastY = this.getY();
      this.update(true);
    });

    this.bindMega();
    this.bindDrawer();
    this.bindHashLinks();
  }

  getY() {
    if (this.usePageWrapper) return Math.max(0, this.pageWrapper.scrollTop);
    return Math.max(0, window.scrollY || document.documentElement.scrollTop || 0);
  }

  measure() {
    const pw = this.pageWrapper;
    this.usePageWrapper = !!pw && getComputedStyle(pw).overflowY !== 'visible' && pw.scrollHeight > pw.clientHeight + 1;
    const group = document.getElementById('header-group');
    this.announceH = group ? group.offsetHeight : 0;
    const bar = this.wrap.querySelector('.ncfh__bar');
    const top = parseFloat(getComputedStyle(this.wrap).top) || 0;
    document.body.style.setProperty('--header-height', top + bar.offsetHeight + 12 + 'px');
    this.offset = -1;
  }

  setState(next) {
    if (this.state === next) return;
    this.state = next;
    this.setAttribute('data-header-state', next);
  }

  update(initial) {
    const y = this.getY();

    const off = Math.max(0, this.announceH - y);
    if (off !== this.offset) {
      this.offset = off;
      this.wrap.style.setProperty('--ncfh-offset', off + 'px');
    }

    const next = this.compact ? y > EXIT_Y : y > ENTER_Y;
    if (next !== this.compact) {
      this.compact = next;
      this.setAttribute('data-compact', String(next));
      if (this.openItem) this.closePanels();
      clearTimeout(this.settleTimer);
      if (initial) {
        this.settleTimer = 0;
        this.setState(next ? 'compact' : 'top');
      } else {
        this.setState('transition');
        this.settleTimer = setTimeout(() => {
          this.settleTimer = 0;
          this.setState(this.compact ? 'compact' : 'top');
        }, cssMs(this, '--nc-motion-slow', 900));
      }
    }

    const dy = y - this.lastY;
    if (Math.abs(dy) >= DIR_THRESHOLD) {
      this.lastY = y;
      const dir = dy > 0 ? 'down' : 'up';
      this.setAttribute('data-header-dir', dir);
      if (this.compact && !this.settleTimer) {
        this.setState(dir === 'down' ? 'scrolling-down' : 'scrolling-up');
      }
      clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => {
        this.setAttribute('data-header-dir', 'idle');
        if (this.compact && !this.settleTimer) this.setState('compact');
      }, 180);
    }
  }

  onResize() {
    this.measure();
    this.lastY = this.getY();
    this.update(true);
    if (window.innerWidth >= DESKTOP_MIN && this.drawerOpen) this.closeDrawer(true);
    if (window.innerWidth < DESKTOP_MIN && this.openItem) this.closePanels();
  }

  /* ---------- Mega menu (desktop) ---------- */
  bindMega() {
    const items = [...this.querySelectorAll('[data-ncfh-item]')];
    items.forEach((li) => {
      const caret = li.querySelector('.ncfh__caret');
      if (!caret) {
        li.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.hoverClose(40);
        });
        return;
      }
      const panel = document.getElementById(caret.getAttribute('aria-controls'));
      li.ncfhPanel = panel;
      caret.addEventListener('click', (event) => {
        event.stopPropagation();
        if (this.openItem === li) {
          this.closePanels();
        } else {
          this.openPanel(li);
          /* Enter / Space produce click with detail 0: move focus into the panel for keyboard users. */
          if (event.detail === 0 && panel) {
            const first = panel.querySelector('a');
            if (first) first.focus({ preventScroll: true });
          }
        }
      });
      li.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(() => this.openPanel(li), 90);
      });
      li.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.hoverClose(200);
      });
      if (panel) {
        panel.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') clearTimeout(this.hoverTimer);
        });
        panel.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse') this.hoverClose(200);
        });
      }
    });

    document.addEventListener('click', (event) => {
      if (this.openItem && !this.wrap.contains(event.target)) this.closePanels();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.openItem) {
        const item = this.openItem;
        this.closePanels();
        const caret = item.querySelector('.ncfh__caret');
        if (caret) caret.focus();
      }
    });
    this.wrap.addEventListener('focusout', (event) => {
      if (this.openItem && event.relatedTarget && !this.wrap.contains(event.relatedTarget)) this.closePanels();
    });
  }

  hoverClose(delay) {
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => this.closePanels(), delay);
  }

  openPanel(li) {
    if (window.innerWidth < DESKTOP_MIN || !li.ncfhPanel) return;
    if (this.openItem && this.openItem !== li) this.closePanels();
    this.openItem = li;
    li.classList.add('is-open');
    li.querySelector('.ncfh__caret').setAttribute('aria-expanded', 'true');
    li.ncfhPanel.classList.add('is-open');
    this.setAttribute('data-mega-open', 'true');
  }

  closePanels() {
    clearTimeout(this.hoverTimer);
    const li = this.openItem;
    if (!li) return;
    li.classList.remove('is-open');
    li.querySelector('.ncfh__caret').setAttribute('aria-expanded', 'false');
    if (li.ncfhPanel) li.ncfhPanel.classList.remove('is-open');
    this.openItem = null;
    this.removeAttribute('data-mega-open');
  }

  /* ---------- Mobile drawer ---------- */
  bindDrawer() {
    if (!this.drawer || !this.menuBtn) return;
    this.menuBtn.addEventListener('click', () => this.openDrawer());
    const closeBtn = this.drawer.querySelector('[data-ncfh-close]');
    if (closeBtn) closeBtn.addEventListener('click', () => this.closeDrawer(false));
    this.drawer.querySelectorAll('.ncfh__d-toggle').forEach((btn) => {
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        const sub = document.getElementById(btn.getAttribute('aria-controls'));
        if (sub) sub.hidden = open;
      });
    });
    this.drawer.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.closeDrawer(true);
    });
    this.drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.closeDrawer(false);
      } else if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    });
  }

  setInert(flag) {
    document.querySelectorAll('#MainContent, footer').forEach((el) => {
      el.inert = flag;
    });
  }

  openDrawer() {
    if (this.drawerOpen) return;
    this.drawerOpen = true;
    this.returnFocus = document.activeElement;
    clearTimeout(this.closeTimer);
    this.drawer.hidden = false;
    void this.drawer.offsetWidth;
    this.drawer.classList.add('is-open');
    this.menuBtn.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('ncfh-lock');
    this.setInert(true);
    const first = this.drawer.querySelector('[data-ncfh-close]');
    if (first) first.focus({ preventScroll: true });
  }

  closeDrawer(skipFocus) {
    if (!this.drawerOpen) return;
    this.drawerOpen = false;
    this.drawer.classList.remove('is-open');
    this.menuBtn.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('ncfh-lock');
    this.setInert(false);
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      if (!this.drawerOpen) this.drawer.hidden = true;
    }, cssMs(this, '--nc-motion-normal', 420) + 30);
    if (!skipFocus && this.returnFocus && this.returnFocus.focus) this.returnFocus.focus({ preventScroll: true });
    if (skipFocus) this.menuBtn.blur();
  }

  trapFocus(event) {
    const nodes = [...this.drawer.querySelectorAll('a[href], button:not([disabled])')].filter(
      (el) => el.offsetParent !== null
    );
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- In-page anchors ---------- */
  /* This theme scrolls inside .page-wrapper on desktop, where native #hash jumps do nothing. */
  scrollToHash(hash, smooth) {
    if (!hash || hash.length < 2) return false;
    let id = hash.slice(1);
    try {
      id = decodeURIComponent(id);
    } catch (error) {
      return false;
    }
    const target = document.getElementById(id);
    if (!target) return false;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: smooth && !reduce ? 'smooth' : 'auto', block: 'start' });
    return true;
  }

  bindHashLinks() {
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
      const link = event.target.closest('a[href*="#"]');
      if (!link) return;
      let url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (error) {
        return;
      }
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
      if (this.scrollToHash(url.hash, true)) {
        event.preventDefault();
        history.pushState(null, '', url.hash);
      }
    });
    if (window.location.hash.length > 1) {
      setTimeout(() => this.scrollToHash(window.location.hash, false), 60);
    }
  }
}

if (!customElements.get('nc-floating-header')) {
  customElements.define('nc-floating-header', NCFloatingHeader);
}
